import type { FeatureDefinitions } from "../capability-model";
import type { CapabilitySelection } from "../types";

export type TypedCapabilitySelection<
  Name extends string,
  Feature extends string,
> = {
  readonly capability: Name;
  readonly features: readonly [Feature];
};

export type CapabilityHandle<
  Name extends string,
  Features extends FeatureDefinitions,
> = {
  readonly name: Name;
  readonly definitions: Features;
  /**
   * Multi-feature selection escape hatch. Prefer the typed property accessors
   * (e.g. `database.user`) at call sites; this exists only for cases where
   * feature names are computed at runtime.
   */
  readonly withFeatures: <F extends keyof Features & string>(
    ...features: readonly F[]
  ) => CapabilitySelection;
} & {
  readonly [Feature in keyof Features & string]: TypedCapabilitySelection<
    Name,
    Feature
  >;
};

export function defineProviderCapability<
  const Name extends string,
  const Features extends FeatureDefinitions,
>(name: Name, definitions: Features): CapabilityHandle<Name, Features> {
  const handle: Record<string, unknown> = {
    name,
    definitions,
    withFeatures: (...features: string[]): CapabilitySelection => ({
      capability: name,
      features: [...features],
    }),
  };
  for (const feature of Object.keys(definitions)) {
    handle[feature] = {
      capability: name,
      features: [feature],
    } satisfies CapabilitySelection;
  }
  return handle as CapabilityHandle<Name, Features>;
}

/**
 * Features declared apart from their capability, so a module can import them
 * without the rest of it (the mobile app's features in `./mobile/`). The
 * capability spreads them back into its definitions.
 */
export function defineFeatures<const Features extends FeatureDefinitions>(
  features: Features,
): Features {
  return features;
}
